const DEFAULT_MAX_SIZE = 200;
const OUTPUT_TYPE = "image/jpeg";
const OUTPUT_QUALITY = 0.9;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load image."));
    image.src = src;
  });

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read image file."));
    reader.readAsDataURL(file);
  });

/**
 * Resize an image file to fit within maxSize x maxSize and return a base64 data URL.
 * Aspect ratio is preserved. Output is JPEG text base64 suitable for API payloads.
 */
export const imageFileToBase64 = async (file, maxSize = DEFAULT_MAX_SIZE) => {
  if (!file) {
    throw new Error("No image file provided.");
  }

  if (!file.type?.startsWith("image/")) {
    throw new Error("Selected file must be an image.");
  }

  const originalDataUrl = await readFileAsDataURL(file);
  const image = await loadImage(originalDataUrl);

  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;

  if (!sourceWidth || !sourceHeight) {
    throw new Error("Unable to determine image dimensions.");
  }

  const scale = Math.min(1, maxSize / sourceWidth, maxSize / sourceHeight);
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to process image.");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, targetWidth, targetHeight);
  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  return canvas.toDataURL(OUTPUT_TYPE, OUTPUT_QUALITY);
};

export default imageFileToBase64;
